import { upperFirst, shuffle } from "lodash-es";
import { routes, endpoints } from "configs";
import { generatePath, request } from "utils";
import {
  Hash,
  StudentAssignment,
  AssignmentAnswer,
  DistrictStudentAssignment,
  Element,
  AssessmentBankQuestion,
} from "types";
import { getQuery, Response } from "@stemscopes/api";
import { LocationDescriptor } from "history";
// import { shuffle } from "shuffle-seed";
import { getPrevSameParts } from "components/specific-features/scopes/duck";
import {
  CONTROLS_LOADING_KEYS,
  PART_TYPE_WITH_LANGUAGE_SUFFIX_SET,
  PART_TYPE_WIRH_JSON_ANSWER_SET,
  PART_TYPE_WITH_MULTI_ANSWER_SET,
} from "./constants";
import * as Types from "./types";

const getElementParts: Types.GetElementParts = (
  assignment?: StudentAssignment,
) =>
  (assignment?.teacherSectionAssignment?.assignmentContentType?.type ===
  "assignmentContentTypeElement"
    ? assignment?.teacherSectionAssignment?.assignmentContentType.element
        .elementParts
    : []
  ).filter(
    ({ partTypeClass }) =>
      partTypeClass !== "PartTypeAnswerKey" &&
      partTypeClass !== "PartTypeStandardsSummary",
  );

export const getQuestions: Types.GetQuestions = ({
  assignment,
  districtAssignment,
  scrambleQuestions,
}) => {
  const isOldQuestions =
    assignment?.teacherSectionAssignment?.assignmentContentType
      ?.teacherAssessment?.old;

  const teacherAssessment =
    assignment?.teacherSectionAssignment?.assignmentContentType
      ?.teacherAssessment;

  const bankQuestionsFromAssignment = scrambleQuestions
    ? shuffle(
        teacherAssessment?.assessmentBankQuestions || [],
        // assignment?.student.id
      )
    : teacherAssessment?.assessmentBankQuestions;

  const questions = scrambleQuestions
    ? shuffle(
        teacherAssessment?.assessmentQuestions || [],
        // assignment?.student.id
      )
    : teacherAssessment?.assessmentQuestions;

  const bankQuestionsFromDistrict =
    districtAssignment?.packageAssessment?.assessmentBankQuestions;

  const bankQuestions = assignment
    ? bankQuestionsFromAssignment
    : bankQuestionsFromDistrict;

  return {
    bankQuestions: isOldQuestions ? [] : bankQuestions,
    questions: isOldQuestions ? questions : [],
    isBilingualAssessment: isOldQuestions
      ? false
      : !!bankQuestions?.every((item) => item.hasSpanishTranslation),
  };
};

export const getElementPartsWithQuestionNumber: Types.GetElementPartsWithQuestionNumber =
  (elementParts, { isStudentAssignment, withSetUpMaterials }) => {
    let questionNumber = 0;

    return elementParts.reduce<
      ReturnType<Types.GetElementPartsWithQuestionNumber>
    >((acc, elementPart) => {
      if (isStudentAssignment && elementPart.teacherOnly) {
        return acc;
      }

      if (
        !(isStudentAssignment || withSetUpMaterials) &&
        elementPart.setUpMaterial === "set_up_student_only"
      ) {
        return acc;
      }

      const hasNumberPart =
        !(withSetUpMaterials && elementPart.setUpMaterial !== "set_up_none") &&
        elementPart.numberPart;

      if (hasNumberPart) {
        questionNumber += 1;

        if (elementPart.restartPartNumbering) {
          questionNumber = 1;
        }
      }

      const newElementPart = {
        ...elementPart,
        questionNumber,
        numberPart: hasNumberPart,
      };

      return [...acc, newElementPart];
    }, []);
  };

const getElementPartSinglePage: Types.GetElementPartsPagination = (
  elementParts,
) => {
  return elementParts.reduce<ReturnType<Types.GetElementPartsPagination>>(
    (result, item, index) => {
      result[result.length - 1].push(item);
      if (item.pageBreak && elementParts.length - 1 !== index) {
        return [...result, []];
      }
      return result;
    },
    [[]],
  );
};

const getElementPartSimplePagination: Types.GetElementPartsPagination = (
  elementParts,
) => {
  return elementParts.map((elementPart) => [elementPart]);
};

const getElementPartNumberPagination: Types.GetElementPartsPagination = (
  elementParts,
) => {
  return elementParts.reduce<ReturnType<Types.GetElementPartsPagination>>(
    (result, item, index) => {
      result[result.length - 1].push(item);
      const nextItem = elementParts[index + 1];
      if (
        nextItem &&
        elementParts.length - 1 !== index &&
        ((nextItem.numberPart && nextItem.restartPartNumbering) ||
          nextItem.questionNumber !== item.questionNumber)
      ) {
        return [...result, []];
      }
      return result;
    },
    [[]],
  );
};

export const getElementPartPages: Types.GetElementPartPages = (
  elementPartsWithQuestionNumber,
  settings = { profileType: "Teacher" },
) => {
  if (settings.profileType === "Teacher") {
    return getElementPartSinglePage(elementPartsWithQuestionNumber);
  }

  switch (settings.presentationType) {
    case 1:
      return getElementPartSimplePagination(elementPartsWithQuestionNumber);
    case 2:
      return getElementPartNumberPagination(elementPartsWithQuestionNumber);
    default:
      return getElementPartSinglePage(elementPartsWithQuestionNumber);
  }
};

const getPreparedPartTypes: Types.getPreparedPartTypes = (elementParts) => {
  return elementParts.map((element, index) => {
    if (
      element.partTypeClass === "PartTypeMultipleChoice" &&
      element?.partType
    ) {
      return {
        ...element,
        partType: {
          ...element.partType,
          ...getPrevSameParts(elementParts, index),
        },
      };
    }
    return element;
  });
};

export const getPreparedElementParts: Types.GetPreparedElementParts = (
  assignment,
  profileType,
) => {
  if (assignment) {
    const elementParts = getElementParts(assignment);
    const { presentationType, withSetUpMaterials = false } =
      assignment?.teacherSectionAssignment?.assignmentContentType?.element ||
      {};
    const preperedElementPartsProps = getPreparedPartTypes(elementParts);
    const elementPartsWithQuestionNumber = getElementPartsWithQuestionNumber(
      preperedElementPartsProps,
      { isStudentAssignment: true, withSetUpMaterials },
    );

    return getElementPartPages(elementPartsWithQuestionNumber, {
      profileType,
      presentationType,
    });
  }

  return [];
};

export const getParams: Types.GetParams = (isStarted) => ({
  include:
    "teacher,student,teacherSection,studentNote," +
    "teacherSectionAssignment,teacherSectionAssignment.assignmentContentType," +
    "teacherSectionAssignment.assignmentContentType.mediaAsset.mediaTags," +
    "teacherSectionAssignment.assignmentContentType.mediaAsset.mediaCategory.mediaTopic," +
    "teacherSectionAssignment.assignmentContentType.element.scope," +
    "teacherSectionAssignment.assignmentContentType.teacherAssessment.assessmentBankQuestions," +
    "teacherSectionAssignment.assignmentContentType.teacherAssessment.assessmentQuestions",
  fields: {
    teacher: "firstName,lastName",
    student: "fullName",
    scope: "name",
    mediaTag: "id,name",
    mediaTopic: "id,name",
    mediaCategory: "id,name,mediaTopic",
    mediaAsset:
      "id,name,date,contentType,videoDuration,videoLink,videoDescription,cover," +
      "video240,video360,video480,video720,mediaCategory,mediaTags,mediaGrades,mediaActivities",
    element:
      "language,scope,scoringMethod,presentationType,withSetUpMaterials,fourFunctionCalculator,scientificCalculator,graphingCalculator,connectedLanguageId",
    teacherSectionAssignment:
      "id,name,allowLateSubmission,assignmentContentType,releaseFeedback,assignmentContentTypeClass,autorelease,afterDueDate,returnNotes,gradable,scrambleQuestions,studentResponses,calculatorType",
    teacherSection: "name",
    teacherAssessment: "id,old,assessmentBankQuestions,assessmentQuestions",
    assessmentQuestion:
      "id,formId,assessmentQuestionContentType,assessmentQuestionContent,shortTypeName,language,gradable",
    assessmentBankQuestion:
      "id,formId,partTypes,languages,assessmentBankQuestionContentType,assessmentBankQuestionContent,hasSpanishTranslation,gradable",
    measurementTable: "measurementTableRows,note",
  },
  isStarted,
});

export const getContentType: Types.GetContentType = (assignment) => ({
  element: assignment?.teacherSectionAssignment?.assignmentContentType?.element,
  teacherAssessment:
    assignment?.teacherSectionAssignment?.assignmentContentType
      ?.teacherAssessment,
});

export const getTeacherRedirectPath: Types.GetTeacherRedirectPath = ({
  teacherSectionId,
  teacherSectionAssignmentId,
  districtAssignmentId,
}) => {
  if (teacherSectionId && teacherSectionAssignmentId) {
    return generatePath(routes.teacherSectionsTeacherSectionAssignment, {
      teacherSectionId,
      teacherSectionAssignmentId,
    });
  }

  if (districtAssignmentId) {
    return generatePath(routes.districtAssignment, {
      id: districtAssignmentId,
    });
  }

  return routes.home;
};

const getNextId: Types.GetNextId = (currentId, assignments) =>
  assignments?.find((assignment) => assignment.id !== currentId)?.id;

export const getStudentRedirectPath: Types.GetStudentRedirectPath = ({
  id,
  type,
  kindergartenDesign,
  startedAssignments,
  notStartedAssignments,
  districtStudentAssignments,
  isStudentFromGoogleClass,
}) => {
  const totalAssignmentsAmount =
    (districtStudentAssignments?.length || 0) +
    (startedAssignments?.length || 0) +
    (notStartedAssignments?.length || 0);

  const path: LocationDescriptor = {
    search: getQuery({
      google_class_room: isStudentFromGoogleClass || undefined,
    }),
  };

  if (type === "turnIn" && kindergartenDesign) {
    if (totalAssignmentsAmount > 1) {
      return routes.assignments;
    }

    if (districtStudentAssignments?.length) {
      const nextId = getNextId(id, districtStudentAssignments);

      if (nextId) {
        return generatePath(routes.districtStudentAssignment, { id: nextId });
      }
    }
    if (startedAssignments?.length) {
      const nextId = getNextId(id, startedAssignments);

      if (nextId) {
        return generatePath(routes.studentAssignment, { id: nextId });
      }
    }

    if (notStartedAssignments?.length) {
      return request
        .get(
          generatePath(endpoints.teacherSectionAssignment, {
            id: notStartedAssignments[0].id,
          }),
        )
        .then((response) => ({
          pathname: response.data.data.redirectTo,
          ...path,
        }))
        .catch(() => routes.assignments);
    }

    return { pathname: routes.home, ...path };
  }

  if (type === "close" && kindergartenDesign) {
    if (totalAssignmentsAmount > 1) {
      return routes.assignments;
    }

    return routes.home;
  }

  return { pathname: routes.assignments, ...path };
};

export const getGrades: Types.GetGrades = (values) => {
  const grades: { [key: string]: string | number } = {};
  Object.keys(values).forEach((key) => {
    if (
      key.match(/teacher_factor/g) &&
      !key.match(/^abq_(\d+)_teacher_factor$/g)
    ) {
      grades[key] = values[key];
    }
  });

  return grades;
};

export const prepareGrades: Types.PrepareGrades = (grades, defaultGrade = 0) =>
  Object.keys(grades).reduce<ReturnType<Types.PrepareGrades>>(
    (acc, key) => ({
      ...acc,
      [key]: grades[key] === null ? defaultGrade : grades[key],
    }),
    {},
  );

export const getFormId: Types.GetFormId = ({ formId, parentFormId }) =>
  (parentFormId ? `${parentFormId}_${formId}` : formId).replace(/_en|_es/g, "");

export const getTeacherFactor: Types.GetTeacherFactor = (
  { formId, parentFormId, type },
  grading,
) => {
  if (!grading) {
    return;
  }

  if (type === "partTypeAssessmentBankQuestion") {
    return grading[formId]?.totalFactor;
  }

  return parentFormId
    ? (grading[parentFormId]?.factor as Hash)?.[`${formId}_teacher_factor`]
    : (grading[formId]?.factor as number);
};

export const getDisplayDidNotAnswer: Types.GetDisplayDidNotAnswer = (
  { formId, parentFormId = "" },
  grading,
) => {
  if (!grading) {
    return false;
  }

  if (typeof grading[parentFormId]?.displayDidNotAnswer === "object") {
    return (
      grading[parentFormId].displayDidNotAnswer as {
        [key: string]: boolean;
      }
    )[formId];
  }

  return !!(
    grading[formId]?.displayDidNotAnswer ||
    grading[parentFormId]?.displayDidNotAnswer
  );
};

export const getGradingNote: Types.GetGradingNote = (
  { formId, parentFormId, type },
  grading,
  stateData,
) => {
  if (type === "partTypeAssessmentBankQuestion") {
    return;
  }

  return (
    (parentFormId
      ? (grading?.[parentFormId]?.feedback as Hash)?.[`${formId}_grading_note`]
      : grading?.[formId]?.feedback) || stateData?.[`${formId}_grading_note`]
  );
};

export const getPreparedValues: Types.GetPreparedValues = (
  preparedElementParts,
  values,
) => {
  let preparedValues = values;

  preparedElementParts.forEach((preparedElementPart) => {
    preparedElementPart.forEach((elementPart) => {
      if (elementPart.partTypeClass === "PartTypeBrainingCamp") {
        const brainingCampCanvas = document.getElementById(
          `bcmInstance_${elementPart.partTypeId}`,
        )?.children[1] as HTMLCanvasElement;
        const base64image = brainingCampCanvas?.hasAttribute("data-interaction")
          ? brainingCampCanvas?.toDataURL()
          : null;

        preparedValues = {
          ...preparedValues,
          [elementPart.partType.formId]: base64image,
        };
      }
    });
  });

  return preparedValues;
};

const getDSAQuestions: Types.GetDSAQuestions = (assessmentBankQuestions) =>
  assessmentBankQuestions.reduce<ReturnType<Types.GetDSAQuestions>>(
    (acc, abq) => {
      if (abq.assessmentBankQuestionContent) {
        return [
          ...acc,
          {
            id: abq.assessmentBankQuestionContentId,
            type: abq.assessmentBankQuestionContentType,
            formId: abq.formId,
          },
        ];
      }

      const partTypes =
        abq.partTypes?.map((partType) => ({
          id: partType.id,
          type: upperFirst(partType.type),
          formId: partType.formId,
          parentFormId: abq.formId,
        })) || [];

      return [...acc, ...partTypes];
    },
    [],
  );

export const getDSAQuestionFormId: Types.GetDSAQuestionFormId = (
  assessmentBankQuestions,
  assignmentAnswer,
) => {
  const questions = getDSAQuestions(assessmentBankQuestions);
  return questions.find(
    (question) =>
      String(question.id) === String(assignmentAnswer.questionId) &&
      question.type === assignmentAnswer.questionType,
  )?.formId;
};

// example: state date of fill in blank has more than one field
// { ptfitbos_12913_50253: "1", ptfitbos_12913_50254: "2" }
const getPartTypeAnswers: Types.GetPartTypeAnswers = (values, formIds) =>
  Object.entries(values).reduce<ReturnType<Types.GetPartTypeAnswers>>(
    (acc, [key, value]) => {
      if (formIds.some((formId) => key.includes(formId))) {
        return { ...acc, [key]: value };
      }
      return acc;
    },
    {},
  );

export const getDSAAnswers: Types.GetDSAAnswers = (
  values,
  assignmentAnswer,
  formId,
) => {
  if (!formId) {
    return null;
  }

  if (PART_TYPE_WITH_MULTI_ANSWER_SET.has(assignmentAnswer.questionType)) {
    const isGriddable =
      assignmentAnswer.questionType === "AssessmentBankQuestionGriddable" ||
      assignmentAnswer.questionType === "PartTypeGriddable";

    // assignmentAnswer.questionId in abq equal abqContentId,
    // in this case need use id from formId
    const formIds = [formId, isGriddable && `q_${formId.split("_")[1]}`].filter(
      Boolean,
    ) as string[];

    return { answer: JSON.stringify(getPartTypeAnswers(values, formIds)) };
  }

  if (PART_TYPE_WIRH_JSON_ANSWER_SET.has(assignmentAnswer.questionType)) {
    return { answer: values[formId] ? JSON.stringify(values[formId]) : null };
  }

  if (PART_TYPE_WITH_LANGUAGE_SUFFIX_SET.has(assignmentAnswer.questionType)) {
    return {
      answer: values[`${formId}_en`] || null,
      esAnswer: values[`${formId}_es`] || null,
    };
  }

  return { answer: values[formId] || null };
};

export const prepareDSAValuesForSave: Types.PrepareDSAValuesForSave = (
  values,
  dsa,
) =>
  dsa.assignmentAnswers.reduce((acc, cur, index) => {
    const formId = getDSAQuestionFormId(
      dsa.districtAssignment.packageAssessment.assessmentBankQuestions || [],
      cur,
    );
    const assignmentAnswer = {
      id: cur.id,
      questionId: cur.questionId,
      questionType: cur.questionType,
      ...getDSAAnswers(values, cur, formId),
    };

    return { ...acc, [index]: assignmentAnswer };
  }, {});

const parseValue = (value: string): any => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

export const getDefaultControlsLoadings: Types.GetDefaultControlsLoadings =
  () =>
    CONTROLS_LOADING_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: 0 }),
      {} as ReturnType<Types.GetDefaultControlsLoadings>,
    );

export const prepareValues = (values: {
  [key: string]: any;
}): { [key: string]: any } => {
  const { assessmentBankQuestions, teacherAssessmentBankQuestions, ...rest } =
    values;
  return {
    ...rest,
    ...(values.assessmentBankQuestions && {
      teacherAssessmentBankQuestionsAttributes:
        values.assessmentBankQuestions.map((v: AssessmentBankQuestion) => ({
          assessment_bank_question_id: Number(v.id),
        })),
    }),
  };
};

export const getDSAValue: Types.GetDSAValue = (assignmentAnswer, formId) => {
  if (assignmentAnswer.answer == null && assignmentAnswer.esAnswer == null) {
    return null;
  }

  if (PART_TYPE_WITH_MULTI_ANSWER_SET.has(assignmentAnswer.questionType)) {
    return parseValue(assignmentAnswer.answer);
  }

  if (PART_TYPE_WIRH_JSON_ANSWER_SET.has(assignmentAnswer.questionType)) {
    return { [formId]: parseValue(assignmentAnswer.answer) };
  }

  if (PART_TYPE_WITH_LANGUAGE_SUFFIX_SET.has(assignmentAnswer.questionType)) {
    return {
      [`${formId}_en`]: assignmentAnswer.answer,
      [`${formId}_es`]: assignmentAnswer.esAnswer,
    };
  }

  return { [formId]: assignmentAnswer.answer };
};

export const prepareDSAValuesFromBackend: Types.PrepareDSAValuesFromBackend = (
  dsa,
) =>
  dsa.assignmentAnswers.reduce((acc, cur) => {
    const formId = getDSAQuestionFormId(
      dsa.districtAssignment.packageAssessment.assessmentBankQuestions || [],
      cur,
    );
    if (!formId) {
      return acc;
    }
    return { ...acc, ...getDSAValue(cur, formId) };
  }, {});

export const prepareReassignedGrades = (
  values: object,
  grade: string | number | undefined,
): object =>
  Object.keys(values).reduce((result, key) => {
    if (key.includes("teacher_factor")) {
      return {
        ...result,
        [key]: Number(grade ?? 0) / 100,
      };
    }
    return result;
  }, {});

export const prepareDistrictAssignmentFromDB = (
  savedAnswers: AssignmentAnswer[],
  dsa: DistrictStudentAssignment,
): DistrictStudentAssignment => {
  return {
    ...dsa,
    assignmentAnswers: dsa.assignmentAnswers.map(
      (answer: AssignmentAnswer) => ({
        ...answer,
        ...(savedAnswers.find(
          (value: any) => value.id === answer.id,
        ) as Object),
      }),
    ),
  };
};

export const getActualElement = (
  response: Response<StudentAssignment>,
): Element => {
  const initialResponse = JSON.parse(response.request.response);
  const actualElement = initialResponse.included.find(
    ({ type, id: itemId }: { type: string; id: string }) =>
      type === "element" && itemId === response.data.data.elementId,
  );
  const { scope } =
    response?.data?.data?.teacherSectionAssignment?.assignmentContentType
      ?.element || {};
  return {
    ...actualElement.meta,
    ...actualElement.attributes,
    id: actualElement.id,
    type: actualElement.type,
    scopeId: scope?.id,
    scope,
  };
};
